import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { create } from 'zustand';
import { Button } from './Button';
import './Toast.css';

/* ------------------------------------------------------------------ *
 * Toasts state what happened, in the same verb the action used.
 * "Publish" produces "Published", not "Success!". Failures say what
 * broke and what to do; they never apologise.
 *
 * They stack bottom-start, above the nav rail rather than over the
 * table — covering the row you just acted on is the one thing a
 * confirmation must not do.
 * ------------------------------------------------------------------ */

export type ToastTone = 'ok' | 'warn' | 'alert' | 'info';

export interface ToastRecord {
  id: string;
  tone: ToastTone;
  message: ReactNode;
  /** Second line: the detail, the count, the reason. */
  detail?: ReactNode;
  /** One inline action, usually Undo. */
  action?: { label: string; onClick: () => void };
  /** Errors stay until dismissed; everything else clears itself. */
  durationMs: number | null;
}

interface ToastStore {
  toasts: ToastRecord[];
  push: (t: Omit<ToastRecord, 'id'>) => string;
  dismiss: (id: string) => void;
}

let seq = 0;

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  push: (t) => {
    const id = `t${++seq}`;
    set((s) => ({ toasts: [...s.toasts, { ...t, id }] }));
    return id;
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

/** Imperative API for mutation callbacks. */
export const toast = {
  ok: (message: ReactNode, opts?: Partial<ToastRecord>) =>
    useToastStore.getState().push({ tone: 'ok', message, durationMs: 4000, ...opts }),
  info: (message: ReactNode, opts?: Partial<ToastRecord>) =>
    useToastStore.getState().push({ tone: 'info', message, durationMs: 4000, ...opts }),
  warn: (message: ReactNode, opts?: Partial<ToastRecord>) =>
    useToastStore.getState().push({ tone: 'warn', message, durationMs: 6000, ...opts }),
  /** Never auto-dismisses. If it mattered enough to fail, it matters enough to read. */
  error: (message: ReactNode, opts?: Partial<ToastRecord>) =>
    useToastStore.getState().push({ tone: 'alert', message, durationMs: null, ...opts }),
  dismiss: (id: string) => useToastStore.getState().dismiss(id),
};

export function ToastViewport() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);

  return (
    <div className="pd-toasts" role="region" aria-label="Notifications">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastItem({ toast: t, onDismiss }: { toast: ToastRecord; onDismiss: () => void }) {
  useEffect(() => {
    if (t.durationMs === null) return;
    const timer = setTimeout(onDismiss, t.durationMs);
    return () => clearTimeout(timer);
  }, [t.durationMs, onDismiss]);

  return (
    <div
      className="pd-toast"
      data-tone={t.tone}
      role={t.tone === 'alert' ? 'alert' : 'status'}
      aria-live={t.tone === 'alert' ? 'assertive' : 'polite'}
    >
      <span className="pd-band" aria-hidden="true" style={{ background: 'var(--tone)' }} />
      <div className="pd-toast__body">
        <p className="pd-toast__message">{t.message}</p>
        {t.detail ? <p className="pd-toast__detail">{t.detail}</p> : null}
      </div>
      {t.action ? (
        <Button
          variant="quiet"
          size="sm"
          onClick={() => {
            t.action?.onClick();
            onDismiss();
          }}
        >
          {t.action.label}
        </Button>
      ) : null}
      <button
        type="button"
        className="pd-toast__close"
        onClick={onDismiss}
        aria-label="Dismiss"
      >
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
          <path d="M4 4l8 8M12 4l-8 8" />
        </svg>
      </button>
    </div>
  );
}
